import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Loader2, Pill, CheckCircle2, ChevronRight, RefreshCw, ClipboardList, EyeOff } from 'lucide-react';
import { EnrichmentDialog } from '../admin/MedicineInventoryPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MedicineTasksPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API_URL}/api/whatsapp/medicine-tasks`, { withCredentials: true });
      setData(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const openMedicine = async (item) => {
    try {
      const { data: med } = await axios.get(`${API_URL}/api/pharmacy/medicines/${item.sku}`, { withCredentials: true });
      setSelected(med);
    } catch {
      setSelected({ sku: item.sku, name: item.name, enrichment: null });
    }
  };

  const handleIgnore = async (e, item) => {
    e.stopPropagation();
    try {
      await axios.post(`${API_URL}/api/whatsapp/medicine-tasks/ignore`, { sku: item.sku }, { withCredentials: true });
      toast.success(`${item.name} won't be suggested for 30 days`);
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to ignore');
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#0E5132]" /></div>;

  const items = data?.items || [];
  const completed = items.filter(i => i.done).length;

  return (
    <div className="max-w-2xl mx-auto space-y-4 px-1" data-testid="medicine-tasks-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-lg sm:text-xl font-heading font-bold text-slate-800 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#0E5132]" /> Today's Medicine Updates
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {data?.total_untouched || 0} medicines still awaiting clinical notes · today's 5 below
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`text-xs ${completed === items.length && items.length > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`} data-testid="tasks-progress">
            {completed}/{items.length} done
          </Badge>
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchTasks(); }} className="rounded-full h-8 w-8 p-0" data-testid="tasks-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            All medicines have clinical notes. Nothing pending today!
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {items.map(item => (
            <Card key={item.sku}
              className={`cursor-pointer transition-colors ${item.done ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 hover:border-[#0E5132]/40'}`}
              onClick={() => openMedicine(item)}
              data-testid={`task-med-${item.sku}`}>
              <CardContent className="py-3 px-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${item.done ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                    {item.done ? <CheckCircle2 className="w-4.5 h-4.5 text-emerald-600" /> : <Pill className="w-4 h-4 text-slate-500" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{item.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{[item.company, item.category].filter(Boolean).join(' · ')} · {item.sku}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {!item.done && (
                    <button
                      onClick={(e) => handleIgnore(e, item)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full border border-slate-200 transition-colors"
                      title="Don't suggest this medicine for 30 days"
                      data-testid={`ignore-${item.sku}`}>
                      <EyeOff className="w-3 h-3" /> Ignore
                    </button>
                  )}
                  {item.done
                    ? <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">Updated</Badge>
                    : <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-600">Awaiting input</Badge>}
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="text-[11px] text-muted-foreground text-center pb-6">
        Tap a medicine and dictate or type your clinical notes — same as the inventory screen.
      </p>

      <EnrichmentDialog
        medicine={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onSaved={fetchTasks}
      />
    </div>
  );
}
